import { FeedModuleTypeEnum, IFeedModule, IGif } from "@/common/services/types";
import { ComponentType, Fragment, memo, useMemo } from "react";
import { BoostedGifModule } from "../BoostedGifModule";
import { SuggestedNichesModule } from "../SuggestedNichesModule";
import { SuggestedCreators } from "../SuggestedCreators";
import { NicheExplorer } from "../NicheExplorer";
import { RelatedTags } from "../RelatedTags";
import { TrendingTagsModule } from "../TrendingTagsModule";
import { TrendingCreatorsModule } from "../TrendingCreatorsModule";
import { TrendingNichesModule } from "../TrendingNichesModule";
import { StreamateCameraModule } from "../StreamateCameraModule";
import { OnlyFansCreatorsModule } from "@/components/ads/OnlyFansCreatorsModule";
import { PaidLinksModule } from "@/components/ads/PaidLinksModule";

import "./FeedModules.scss";

interface IFeedModuleEntry {
  module: IFeedModule;
  counter: number;
}

interface IProps {
  modules: IFeedModuleEntry[] | null;
  gifs: IGif[];
  boostedGifs?: IGif[];
  position: number;
}

interface IFeedModuleTypeProps {
  gifs: IGif[];
  boostedGifs?: IGif[];
  counter: number;
  position: number;
  moduleId: string;
  moduleType: FeedModuleTypeEnum;
}

type ModuleRenderer = ComponentType<IFeedModuleTypeProps>;

const modulesMap: Record<FeedModuleTypeEnum, ModuleRenderer> = {
  [FeedModuleTypeEnum.BOOST]: ({ boostedGifs, counter, position, moduleId, moduleType }) =>
    <BoostedGifModule
      boostedGifs={boostedGifs}
      counter={counter}
      position={position}
      moduleId={moduleId}
      moduleType={moduleType}
    />,
  [FeedModuleTypeEnum.SUGGESTED_NICHES]: ({ moduleId, moduleType }) =>
    <SuggestedNichesModule
      page={1}
      moduleId={moduleId}
      moduleType={moduleType}
    />,
  [FeedModuleTypeEnum.SECONDARY_SUGGESTED_NICHES]: ({ moduleId, moduleType }) =>
    <SuggestedNichesModule
      page={2}
      moduleId={moduleId}
      moduleType={moduleType}
    />,
  [FeedModuleTypeEnum.SUGGESTED_CREATORS]: ({ moduleId, moduleType }) =>
    <SuggestedCreators moduleId={moduleId} moduleType={moduleType} />,
  [FeedModuleTypeEnum.TRENDING_TAGS]: ({ moduleId, moduleType }) =>
    <TrendingTagsModule moduleId={moduleId} moduleType={moduleType} />,
  [FeedModuleTypeEnum.TRENDING_CREATORS]: ({ moduleId, moduleType }) =>
    <TrendingCreatorsModule moduleId={moduleId} moduleType={moduleType} />,
  [FeedModuleTypeEnum.TRENDING_NICHES]: ({ moduleId, moduleType }) =>
    <TrendingNichesModule moduleId={moduleId} moduleType={moduleType} />,
  [FeedModuleTypeEnum.NICHE_EXPLORER]: ({ gifs, moduleId, moduleType }) =>
    <NicheExplorer gifs={gifs} moduleId={moduleId} moduleType={moduleType} />,
  [FeedModuleTypeEnum.RELATED_TAGS]: ({ gifs, moduleId, moduleType }) =>
    <RelatedTags gifs={gifs} moduleId={moduleId} moduleType={moduleType} />,
  [FeedModuleTypeEnum.LIVE_CAM]: ({ moduleId, moduleType, position }) =>
    <StreamateCameraModule moduleId={moduleId} moduleType={moduleType} position={position} />,
  [FeedModuleTypeEnum.ONLY_FANS]: ({ moduleId, moduleType }) =>
    <OnlyFansCreatorsModule moduleId={moduleId} moduleType={moduleType} />,
  [FeedModuleTypeEnum.PAID_LINKS]: ({ moduleId, moduleType }) =>
    <PaidLinksModule moduleId={moduleId} moduleType={moduleType} />,
};

const buildModuleId = (moduleType: FeedModuleTypeEnum, counter: number, position: number) => {
  return `feed-module-${moduleType}-${counter}-pos-${position}`;
};

const FeedModulesComponent = ({ modules, gifs, boostedGifs, position }: IProps) => {
  const renderedModules = useMemo(() => {
    if (!modules || modules.length === 0) {
      return null;
    }

    return modules.map(({ module, counter }) => {
      const ModuleComponent = modulesMap[module.type];
      const moduleId = buildModuleId(module.type, counter, position);

      if (ModuleComponent === undefined) {
        return (<Fragment key={moduleId} />);
      }

      return (
        <ModuleComponent
          key={moduleId}
          gifs={gifs}
          boostedGifs={boostedGifs}
          position={position}
          counter={counter}
          moduleId={moduleId}
          moduleType={module.type}
        />
      );
    });
  }, [modules, gifs, boostedGifs, position]);

  return renderedModules ? <>{renderedModules}</> : null;
};

export const FeedModules = memo(FeedModulesComponent);
